import {
    IconCheck,
    IconPencil,
    IconPlus,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { usePdfEditorStore } from '../state/usePdfEditorStore';
import Hint from '../../../components/common/Hint';
import { PanelCloseButton } from '../ui/prims';

const KIND_ICON = { add: IconPlus, edit: IconPencil, delete: IconTrash };
const KIND_TONE = {
    add: 'text-[#1E9E5E] bg-[#E3F6EC] dark:bg-emerald-950/30',
    edit: 'text-[#B7791F] bg-[#FDF6EC] dark:bg-amber-950/30',
    delete: 'text-[#D33A2C] bg-[#FDECEA] dark:bg-red-950/30',
};

/**
 * The reviewing pane: every change waiting on a decision.
 *
 * One row per revision, with the two buttons that are the whole point — take it
 * or put it back. Clicking the row goes to the thing that changed, because a
 * list of edits you cannot see is not a review.
 */
export default function ReviewingPanel() {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const revisions = usePdfEditorStore((s) => s.revisions);
    const pages = usePdfEditorStore((s) => s.pages);
    const goToPage = usePdfEditorStore((s) => s.goToPage);
    const selectObject = usePdfEditorStore((s) => s.selectObject);
    const resolveRevision = usePdfEditorStore((s) => s.resolveRevision);
    const resolveAllRevisions = usePdfEditorStore((s) => s.resolveAllRevisions);
    const toggleReviewing = usePdfEditorStore((s) => s.toggleReviewing);

    const digits = (n) => (isBn ? toBnDigits(n) : String(n));
    const pageOf = (uid) => pages.findIndex((page) => page.uid === uid);

    return (
        <aside className="flex h-full w-[248px] flex-none flex-col border-l border-[#ECE8E4] bg-white dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex h-10 flex-none items-center justify-between px-3 text-[12px] font-bold text-[#3D3833] dark:text-zinc-200">
                {t('pdfEditor.review.pane')}
                <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-[#A8A099]">{digits(revisions.length)}</span>
                    <PanelCloseButton onClick={toggleReviewing} />
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2 [scrollbar-width:thin]">
                {revisions.length === 0 ? (
                    <p className="px-2 py-6 text-center text-[11.5px] leading-relaxed text-[#A8A099] dark:text-zinc-500">
                        {t('pdfEditor.review.noChanges')}
                    </p>
                ) : revisions.map((revision) => {
                    const Icon = KIND_ICON[revision.kind] || IconPencil;
                    const page = pageOf(revision.uid);
                    return (
                        <div
                            key={revision.id}
                            className="mb-1 flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-[#F7F5F3] dark:hover:bg-zinc-800"
                        >
                            <span className={`flex h-6 w-6 flex-none items-center justify-center rounded-md ${KIND_TONE[revision.kind]}`}>
                                <Icon size={13} stroke={2} />
                            </span>

                            <button
                                type="button"
                                onClick={() => {
                                    if (page >= 0) goToPage(page);
                                    selectObject(revision.objectId);
                                }}
                                className="min-w-0 flex-1 text-left"
                            >
                                <span className="block truncate text-[12px] font-medium text-[#3D3833] dark:text-zinc-200">
                                    {t(`pdfEditor.review.kinds.${revision.kind}`)}
                                </span>
                                <span className="block text-[10.5px] text-[#A8A099]">
                                    {t('pdfEditor.review.onPage', { page: digits(page + 1) })}
                                </span>
                            </button>

                            <div className="flex flex-none items-center gap-0.5">
                                <Hint label={t('pdfEditor.review.accept')}>
                                    <button
                                        type="button"
                                        onClick={() => resolveRevision(revision.id, true)}
                                        className="flex h-6 w-6 items-center justify-center rounded text-[#1E9E5E] hover:bg-[#E3F6EC] dark:hover:bg-emerald-950/40"
                                    >
                                        <IconCheck size={14} stroke={2.2} />
                                    </button>
                                </Hint>
                                <Hint label={t('pdfEditor.review.reject')}>
                                    <button
                                        type="button"
                                        onClick={() => resolveRevision(revision.id, false)}
                                        className="flex h-6 w-6 items-center justify-center rounded text-[#D33A2C] hover:bg-[#FDECEA] dark:hover:bg-red-950/40"
                                    >
                                        <IconX size={14} stroke={2.2} />
                                    </button>
                                </Hint>
                            </div>
                        </div>
                    );
                })}
            </div>

            {revisions.length ? (
                <div className="flex flex-none gap-1.5 border-t border-[#ECE8E4] p-2 dark:border-zinc-800">
                    <button
                        type="button"
                        onClick={() => resolveAllRevisions(true)}
                        className="h-8 flex-1 rounded-lg bg-[#1E9E5E] text-[12px] font-semibold text-white transition-colors hover:bg-[#178A50]"
                    >
                        {t('pdfEditor.review.acceptAll')}
                    </button>
                    <button
                        type="button"
                        onClick={() => resolveAllRevisions(false)}
                        className="h-8 flex-1 rounded-lg border border-[#ECE8E4] text-[12px] font-semibold text-[#D33A2C] transition-colors hover:bg-[#FDECEA] dark:border-zinc-700"
                    >
                        {t('pdfEditor.review.rejectAll')}
                    </button>
                </div>
            ) : null}
        </aside>
    );
}
